// Draft Management Utilities for Announcements
// Handles localStorage persistence for drafts and preferences

import { AudienceType } from '../types/communication';

// ============================================
// TYPES
// ============================================

export interface AnnouncementDraft {
  id: string;
  name?: string; // Optional custom name for saved drafts
  activeTab: 'STUDENTS' | 'EMPLOYEES';
  audienceType: AudienceType | '';
  selectedTargets: string[];
  title: string;
  body: string;
  showAsPopup: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AudiencePreference {
  activeTab: 'STUDENTS' | 'EMPLOYEES';
  audienceType: AudienceType | '';
  selectedTargets: string[];
  lastUsed: string;
}

// ============================================
// CONSTANTS
// ============================================

const STORAGE_KEYS = {
  AUTO_DRAFT: 'scholar_minds_announcement_draft',
  SAVED_DRAFTS: 'scholar_minds_saved_drafts',
  LAST_AUDIENCE: 'scholar_minds_last_audience',
} as const;

const AUTO_SAVE_INTERVAL = 10000; // 10 seconds

// ============================================
// AUTO-DRAFT (Single auto-saved draft)
// ============================================

export const saveAutoDraft = (draft: Omit<AnnouncementDraft, 'id' | 'createdAt' | 'updatedAt'>): void => {
  try {
    const existingDraft = getAutoDraft();
    const now = new Date().toISOString();
    
    const draftToSave: AnnouncementDraft = {
      id: existingDraft?.id || `auto_${Date.now()}`,
      ...draft,
      createdAt: existingDraft?.createdAt || now,
      updatedAt: now,
    };

    localStorage.setItem(STORAGE_KEYS.AUTO_DRAFT, JSON.stringify(draftToSave));
  } catch (error) {
    console.error('Failed to save auto-draft:', error);
  }
};

export const getAutoDraft = (): AnnouncementDraft | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.AUTO_DRAFT);
    if (!stored) return null;
    
    const draft = JSON.parse(stored) as AnnouncementDraft;
    
    // Check if draft is older than 7 days
    const createdAt = new Date(draft.createdAt);
    const now = new Date();
    const daysDiff = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
    
    if (daysDiff > 7) {
      clearAutoDraft();
      return null;
    }
    
    return draft;
  } catch (error) {
    console.error('Failed to get auto-draft:', error);
    return null;
  }
};

export const clearAutoDraft = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTO_DRAFT);
  } catch (error) {
    console.error('Failed to clear auto-draft:', error);
  }
};

export const hasAutoDraft = (): boolean => {
  const draft = getAutoDraft();
  // Only consider it a draft if there's actual content
  return draft !== null && (draft.title.trim() !== '' || draft.body.trim() !== '');
};

// ============================================
// SAVED DRAFTS (Multiple named drafts)
// ============================================

export const saveDraft = (
  draft: Omit<AnnouncementDraft, 'id' | 'createdAt' | 'updatedAt'>,
  name?: string
): string => {
  try {
    const drafts = getSavedDrafts();
    const now = new Date().toISOString();
    const id = `draft_${Date.now()}`;
    
    const newDraft: AnnouncementDraft = {
      id,
      name: name || `Draft ${drafts.length + 1}`,
      ...draft,
      createdAt: now,
      updatedAt: now,
    };

    drafts.push(newDraft);
    localStorage.setItem(STORAGE_KEYS.SAVED_DRAFTS, JSON.stringify(drafts));
    
    return id;
  } catch (error) {
    console.error('Failed to save draft:', error);
    throw new Error('Could not save draft');
  }
};

export const getSavedDrafts = (): AnnouncementDraft[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SAVED_DRAFTS);
    if (!stored) return [];
    
    const drafts = JSON.parse(stored) as AnnouncementDraft[];
    
    // Sort by most recent first
    return drafts.sort((a, b) => 
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  } catch (error) {
    console.error('Failed to get saved drafts:', error);
    return [];
  }
};

export const getSavedDraft = (id: string): AnnouncementDraft | null => {
  try {
    const drafts = getSavedDrafts();
    return drafts.find(d => d.id === id) || null;
  } catch (error) {
    console.error('Failed to get saved draft:', error);
    return null;
  }
};

export const updateDraft = (id: string, updates: Partial<AnnouncementDraft>): void => {
  try {
    const drafts = getSavedDrafts();
    const index = drafts.findIndex(d => d.id === id);
    
    if (index === -1) {
      throw new Error('Draft not found');
    }
    
    drafts[index] = {
      ...drafts[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    
    localStorage.setItem(STORAGE_KEYS.SAVED_DRAFTS, JSON.stringify(drafts));
  } catch (error) {
    console.error('Failed to update draft:', error);
    throw new Error('Could not update draft');
  }
};

export const deleteDraft = (id: string): void => {
  try {
    const drafts = getSavedDrafts();
    const filtered = drafts.filter(d => d.id !== id);
    localStorage.setItem(STORAGE_KEYS.SAVED_DRAFTS, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to delete draft:', error);
    throw new Error('Could not delete draft');
  }
};

// ============================================
// AUDIENCE PREFERENCE (Remember last used)
// ============================================

export const saveAudiencePreference = (preference: Omit<AudiencePreference, 'lastUsed'>): void => {
  try {
    const prefToSave: AudiencePreference = {
      ...preference,
      lastUsed: new Date().toISOString(),
    };
    
    localStorage.setItem(STORAGE_KEYS.LAST_AUDIENCE, JSON.stringify(prefToSave));
  } catch (error) {
    console.error('Failed to save audience preference:', error);
  }
};

export const getAudiencePreference = (): AudiencePreference | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.LAST_AUDIENCE);
    if (!stored) return null;
    
    return JSON.parse(stored) as AudiencePreference;
  } catch (error) {
    console.error('Failed to get audience preference:', error);
    return null;
  }
};

export const clearAudiencePreference = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.LAST_AUDIENCE);
  } catch (error) {
    console.error('Failed to clear audience preference:', error);
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

export const getStorageStats = () => {
  return {
    hasAutoDraft: hasAutoDraft(),
    savedDraftsCount: getSavedDrafts().length,
    hasAudiencePreference: getAudiencePreference() !== null,
  };
};

export const clearAllAnnouncementData = (): void => {
  clearAutoDraft();
  localStorage.removeItem(STORAGE_KEYS.SAVED_DRAFTS);
  clearAudiencePreference();
};
